import {
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { DATATYPES } from 'ontraccr-common/lib/Definitions';
import {
  HiddenSectionPreload,
} from 'ontraccr-common';

import Permissions from '../../auth/Permissions';
import { getIdMap } from '../../helpers/helpers';
import {
  getRelevantDropdownValues,
  parseCustomDataFromTemplate,
} from '../FormBuilder/FormFields/TimeEntryTable/TimeEntryTable.helpers';
import { getSubContractsWithTotalChangesMap } from '../../projects/ProjectScheduleOfValues/helpers';
import { getProjectIdMap } from '../state/forms.selectors';

const {
  buildTimecardNativeFieldResponses,
  getTimecardTemplateSections,
  getTimecardSectionPermissionMap,
  enrichResponsesWithHiddenSectionPreload,
} = HiddenSectionPreload;

const buildSubContractRowMap = (subContractsWithTotalChanges = []) => (
  subContractsWithTotalChanges.reduce((acc, subContract) => {
    acc[subContract.rowId] = subContract.id;
    return acc;
  }, {})
);

const buildTimeEntryAttributeContext = ({
  customData = {},
  task = {},
  getRelevantIds,
  authorId,
  projectIdMap = {},
  customerIdMap = {},
  vendorIdMap = {},
  costcodeIdMap = {},
  userIdMap = {},
  scheduleOfValuesSummary = {},
  globalAddressBooks = {},
  subContractMap = {},
  subContractCOMap = {},
  payablesSubContractMap = {},
  bucketIdMap = {},
  equipment = [],
  projectEquipment = [],
  equipmentTypeMap = {},
}) => {
  const resolveRelevantIds = getRelevantIds
    ?? ((data) => getRelevantDropdownValues(data, { multiple: true }));
  const relevantIds = resolveRelevantIds(customData) ?? {};
  const projectId = task.projectId ?? relevantIds?.projectIds?.[0];
  const linkedProjectIds = projectId ? [projectId] : (relevantIds?.projectIds ?? []);

  const subContractsWithTotalChanges = getSubContractsWithTotalChangesMap({
    projectIds: linkedProjectIds,
    subContractMap,
    subContractCOMap,
  });

  return {
    authorId: task.userId ?? authorId,
    selectedProject: projectId ? {
      projectId,
      customerId: projectIdMap[projectId]?.customerId,
    } : undefined,
    timecardProjectId: projectId,
    projectId,
    getRelevantIds: resolveRelevantIds,
    subContractRowMap: buildSubContractRowMap(subContractsWithTotalChanges),
    costcodeCustomFieldValues: {},
    extraProps: {
      equipment,
      projectEquipment,
      equipmentTypeMap,
    },
    dataByType: {
      [DATATYPES.Customer]: [customerIdMap, relevantIds?.customerIds ?? []],
      [DATATYPES.Contact]: [globalAddressBooks, relevantIds?.contactIds ?? []],
      [DATATYPES.ProjectContract]: [scheduleOfValuesSummary, linkedProjectIds],
      [DATATYPES.Project]: [projectIdMap, linkedProjectIds],
      [DATATYPES.Vendor]: [vendorIdMap, relevantIds?.vendorIds ?? []],
      [DATATYPES.Costcodes]: [costcodeIdMap, relevantIds?.costcodeIds ?? []],
      [DATATYPES.User]: [userIdMap, relevantIds?.userIds ?? []],
      [DATATYPES.SubContract]: [subContractsWithTotalChanges, relevantIds?.subContractIds ?? []],
      [DATATYPES.Buckets]: [bucketIdMap, []],
      [DATATYPES.PayableSubContracts]: [
        payablesSubContractMap,
        relevantIds?.payableSubContractIds ?? [],
      ],
    },
  };
};

const enrichTimeEntryCustomData = ({
  taskCustomFields = {},
  divisionId,
  customFields = {},
  templateCustomData = {},
  task = {},
  userId,
  positionId,
  userTeamIds = [],
  attributeMaps = {},
}) => {
  if (!divisionId) {
    return parseCustomDataFromTemplate(taskCustomFields, templateCustomData);
  }

  const templateSections = getTimecardTemplateSections({ customFields, divisionId });
  const sectionPermissionMap = getTimecardSectionPermissionMap({
    customFields,
    divisionId,
    userId,
    positionId,
    userTeamIds,
  });
  const parsedCustomData = parseCustomDataFromTemplate(taskCustomFields, templateCustomData);
  const nativeFieldResponses = buildTimecardNativeFieldResponses(task, {
    costcodeIdMap: attributeMaps.costcodeIdMap,
  });
  const responsesWithNativeFields = {
    ...parsedCustomData,
    ...nativeFieldResponses,
  };
  const attributeContext = buildTimeEntryAttributeContext({
    customData: responsesWithNativeFields,
    task,
    authorId: userId,
    ...attributeMaps,
  });
  const enrichedResponses = enrichResponsesWithHiddenSectionPreload({
    responses: responsesWithNativeFields,
    sections: templateSections,
    sectionPermissionMap,
    attributeContext,
  });

  return parseCustomDataFromTemplate(enrichedResponses, templateCustomData);
};

export const useEnrichTimeEntryCustomData = ({
  subContractMap = {},
  subContractCOMap = {},
  scheduleOfValuesSummary = {},
  projectIdMap: projectIdMapOverride,
  userIdMap: userIdMapOverride,
} = {}) => {
  const projectIdMapFromState = useSelector(getProjectIdMap);
  const customerIdMap = useSelector((state) => state.customers.customers);
  const vendorIdMap = useSelector((state) => state.vendors.vendors);
  const globalAddressBooks = useSelector((state) => state.contacts.globalAddressBooks);
  const equipment = useSelector((state) => state.equipment.equipment);
  const projectEquipment = useSelector((state) => state.equipment.projectEquipment);
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);
  const users = useSelector((state) => state.users.users);
  const currentUser = useSelector((state) => state.users.currentUser);
  const userTeams = useSelector((state) => state.users.userTeams);
  const buckets = useSelector((state) => state.buckets.buckets);
  const payablesSubContractMap = useSelector((state) => state.forms.subContractMap);
  const positionNames = useSelector((state) => state.settings.positionNames);

  const projectIdMap = projectIdMapOverride ?? projectIdMapFromState;
  const userIdMap = useMemo(
    () => userIdMapOverride ?? getIdMap(users),
    [userIdMapOverride, users],
  );
  const equipmentTypeMap = useMemo(() => getIdMap(equipmentTypes), [equipmentTypes]);
  const bucketIdMap = useMemo(() => getIdMap(buckets ?? []), [buckets]);
  const positionNameMap = useMemo(() => getIdMap(positionNames, 'name'), [positionNames]);

  const userPositionId = useMemo(() => {
    const userId = currentUser?.id ?? Permissions.id;
    const userPosition = userIdMap[userId]?.position;
    return positionNameMap[userPosition]?.id;
  }, [currentUser?.id, positionNameMap, userIdMap]);

  return useCallback(({
    taskCustomFields,
    divisionId,
    customFields,
    templateCustomData,
    task,
    userId,
    positionId,
    userTeamIds,
    payablesSubContractMapOverride,
    costcodeIdMap,
  }) => enrichTimeEntryCustomData({
    taskCustomFields,
    divisionId,
    customFields,
    templateCustomData,
    task,
    userId: userId ?? currentUser?.id ?? Permissions.id,
    positionId: positionId ?? userPositionId,
    userTeamIds: userTeamIds ?? userTeams[Permissions.id] ?? [],
    attributeMaps: {
      projectIdMap,
      customerIdMap,
      vendorIdMap,
      costcodeIdMap,
      userIdMap,
      scheduleOfValuesSummary,
      globalAddressBooks,
      subContractMap,
      subContractCOMap,
      payablesSubContractMap: payablesSubContractMapOverride ?? payablesSubContractMap,
      bucketIdMap,
      equipment,
      projectEquipment,
      equipmentTypeMap,
    },
  }), [
    bucketIdMap,
    currentUser?.id,
    customerIdMap,
    equipment,
    equipmentTypeMap,
    globalAddressBooks,
    payablesSubContractMap,
    projectEquipment,
    projectIdMap,
    scheduleOfValuesSummary,
    subContractCOMap,
    subContractMap,
    userIdMap,
    userPositionId,
    userTeams,
    vendorIdMap,
  ]);
};
